import { type SyntheticEvent, useEffect, useState } from 'react'
import HelpOutlineOutlined from '@mui/icons-material/HelpOutlineOutlined'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormControlLabel from '@mui/material/FormControlLabel'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Switch from '@mui/material/Switch'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import Typography from '@mui/material/Typography'
import SaveIcon from '@mui/icons-material/Save'
import { LogLevel } from 'common/api/v1/types'
import type { BuildInfo } from 'common-billing-server/types'

import { useBillingApi } from '../../api/billing/billing-context'
import { listResult } from '../../list-result'
import { AutoComplete } from '../common/AutoComplete'
import Tooltip from 'common-ui/components/Tooltip'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { ApplianceLicenseProducts } from './ApplianceLicenseProducts'
import { NetSuiteSync } from './NetSuiteSync'

enum SettingsTab {
    settings = 'settings',
    keys = 'keys',
    licenseProducts = 'licenseProducts',
    netsuite = 'netsuite',
}

export const EditGlobalSettingsPage = () => {
    const billingApi = useBillingApi()
    const [logLevel, setLogLevel] = useState<LogLevel | null>(null)
    const [mfaEnforced, setMfaEnforced] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [settingsTab, setSettingsTab] = useState(SettingsTab.settings)
    const [buildInfo, setBuildInfo] = useState<BuildInfo>()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()

    useEffect(() => {
        const operationId = 'fetch-global-settings'

        async function fetchGlobalSettings() {
            removeFailedOperation(operationId)
            setIsLoading(true)
            try {
                const settings = await billingApi.getGlobalsettings()
                setLogLevel(settings.logLevel)
                setMfaEnforced(settings.mfaEnforced)
            } catch (error) {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching global settings',
                    error,
                })
            } finally {
                setIsLoading(false)
            }
        }
        void fetchGlobalSettings()
    }, [billingApi])

    useEffect(() => {
        const operationId = 'fetch-build-info'
        void billingApi
            .getBuildInfo()
            .then(setBuildInfo)
            .catch((error) => {
                addFailedOperation({
                    id: operationId,
                    message: 'Failed fetching build info',
                    error,
                })
            })
    }, [billingApi])

    const saveGlobalSettings = async (event: SyntheticEvent) => {
        event.preventDefault()
        if (logLevel === null) return

        const operationId = 'update-global-settings'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            const currentSettings = await billingApi.getGlobalsettings()
            await billingApi.setGlobalsettings({ ...currentSettings, logLevel, mfaEnforced })
        } catch (error) {
            addFailedOperation({
                id: operationId,
                message: 'Failed updating global settings',
                error,
            })
        } finally {
            setIsLoading(false)
        }
    }

    const logLevelValue = logLevel !== null ? { id: logLevel.toString(), displayName: LogLevel[logLevel] } : null
    const options = Object.keys(LogLevel)
        .filter((level) => Number.isInteger(Number(level)))
        .map((id) => ({ id, displayName: LogLevel[Number(id)] }))

    return (
        <Wrapper>
            <Paper>
                <Tabs value={settingsTab} variant="fullWidth" onChange={(_event, value) => setSettingsTab(value)}>
                    <Tab label="Settings" value={SettingsTab.settings} />
                    <Tab label="Keys" value={SettingsTab.keys} />
                    <Tab label="License products" value={SettingsTab.licenseProducts} />
                    <Tab label="NetSuite" value={SettingsTab.netsuite} />
                </Tabs>
                {settingsTab === SettingsTab.settings ? (
                    <form onSubmit={(event) => void saveGlobalSettings(event)}>
                        <Grid container spacing={2} sx={{ p: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <AutoComplete<{ id: string; displayName: string }>
                                    dataTestId="log-level"
                                    placeholder="Log level"
                                    value={logLevelValue}
                                    isRequired
                                    onValueSelected={(level) => {
                                        if (level?.id && level.id in LogLevel) {
                                            setLogLevel(Number(level.id) as LogLevel)
                                        }
                                    }}
                                    formatSelectedValue={(level) => level.displayName}
                                    isClearable
                                    api={() => Promise.resolve(listResult(options))}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={mfaEnforced}
                                            onChange={(event) => setMfaEnforced(event.target.checked)}
                                        />
                                    }
                                    label={
                                        <Box
                                            component="span"
                                            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                        >
                                            Enforce MFA for password sign-ins
                                            <Tooltip title="Users signing in with a password must use or set up an authenticator app. Verified passkeys and Microsoft Entra ID sign-ins remain governed by their own MFA policy.">
                                                <HelpOutlineOutlined fontSize="small" color="action" />
                                            </Tooltip>
                                        </Box>
                                    }
                                />
                            </Grid>

                            <Grid size={{ xs: 12 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={logLevel === null || isLoading}
                                        startIcon={
                                            isLoading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />
                                        }
                                    >
                                        Update
                                    </Button>
                                </Box>
                            </Grid>
                            {errorBanner}
                        </Grid>
                    </form>
                ) : null}
                {settingsTab === SettingsTab.keys ? (
                    <Grid container spacing={2} sx={{ p: 2, justifyContent: 'flex-start', alignItems: 'center' }}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Typography variant="h6">Entitlements public key hash:</Typography>
                            <Typography variant="body2">{buildInfo?.entitlementsPublicKeyHash ?? ''}</Typography>
                            <Button
                                onClick={() =>
                                    void navigator.clipboard.writeText(buildInfo?.entitlementsPublicKey ?? '')
                                }
                            >
                                Copy full key
                            </Button>
                        </Grid>
                    </Grid>
                ) : null}
                {settingsTab === SettingsTab.licenseProducts ? <ApplianceLicenseProducts /> : null}
                {settingsTab === SettingsTab.netsuite ? <NetSuiteSync /> : null}
            </Paper>
        </Wrapper>
    )
}
